/**
 * antd message/notification 桥接（utils 单例）。
 *
 * antd v5 静态 message/notification 无法消费 ConfigProvider 动态主题（官方弃用告警），
 * 官方方案为 <App> 组件 + App.useApp() 动态实例。本模块导出与静态用法同形的
 * message/notification：根组件 AntdMessageBinder 绑定动态实例后全局调用走主题感知
 * 实例；未绑定（测试环境/首帧）回落 antd 静态实现，行为与迁移前一致——
 * vi.mock('antd') 的 spec 断言经回落路径照常命中。
 */
import { message as antdMessage, notification as antdNotification } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type { NotificationInstance } from 'antd/es/notification/interface';

let messageInstance: MessageInstance | null = null;
let notificationInstance: NotificationInstance | null = null;

/** 根组件 Binder 挂载时调用：绑定 App.useApp() 动态实例（幂等）。 */
export function bindMessageApi(
  message: MessageInstance,
  notification: NotificationInstance,
): void {
  messageInstance = message;
  notificationInstance = notification;
}

/** 测试辅助：解除绑定，回落 antd 静态实现。 */
export function unbindMessageApi(): void {
  messageInstance = null;
  notificationInstance = null;
}

/** 构造桥接代理：调用期读取最新实例，未绑定回落 fallback（含 vi.mock 替换后的静态对象）。 */
function bridge<T extends object>(fallback: T, getDynamic: () => T | null): T {
  return new Proxy(fallback, {
    get(_target, prop) {
      const source = getDynamic() ?? fallback;
      const value = Reflect.get(source, prop) as unknown;
      return typeof value === "function"
        ? (value as (...args: unknown[]) => unknown).bind(source)
        : value;
    },
  });
}

/** message 桥接（与 antd MessageInstance 同形，直接替换 `import { message } from 'antd'`）。 */
export const message: MessageInstance = bridge(antdMessage, () => messageInstance);

/** notification 桥接（与 antd NotificationInstance 同形）。 */
export const notification: NotificationInstance = bridge(
  antdNotification,
  () => notificationInstance,
);
